import React, { useMemo } from 'react';
import { EnhancedDeal } from '../../types';
import { getSourceColor } from '../../utils/sourceColors';
import { getProjectDisplayName } from '../../utils/projectName';
import { Crosshair } from 'lucide-react';
import './DealCard.css';

const getDealDaySettings = () => {
  try {
    const saved = localStorage.getItem('crm_deal_day_settings');
    if (saved) return JSON.parse(saved);
  } catch (e) { }
  return { freshDays: 7, normalDays: 30, agingDays: 60 };
};

const isValidSource = (source: string | undefined): boolean => {
  return !!source && source !== 'unspecified' && source.trim() !== '';
};

interface DealCardProps {
  deal: EnhancedDeal;
  isRotting: boolean;
  daysInStage: number;
  isDragging: boolean;
  leadSource?: string;
  displayValue?: number;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  compact?: boolean;
}

export const DealCard: React.FC<DealCardProps> = ({
  deal,
  isRotting,
  daysInStage,
  isDragging,
  leadSource,
  displayValue,
  onClick,
  onDragStart,
  onDragEnd,
  compact = false,
}) => {
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
    return `$${Math.round(value).toLocaleString()}`;
  };

  const formatDays = (days: number): string => {
    if (days < 1) return '<1d';
    return `${days}d`;
  };

  const daySettings = useMemo(() => getDealDaySettings(), []);

  const getDaysBadgeClass = (): string => {
    if (daysInStage < 1) return 'dc-badge-fresh';
    if (daysInStage <= daySettings.freshDays) return 'dc-badge-good';
    if (daysInStage <= daySettings.normalDays) return 'dc-badge-normal';
    if (daysInStage <= daySettings.agingDays) return 'dc-badge-aging';
    return 'dc-badge-stale';
  };

  const displayName = getProjectDisplayName(deal);
  const isKit = (deal as any).designType === 'kit' ||
    (deal.title && deal.title.toLowerCase().includes('kit'));

  const sourceColor = (() => {
    if (isValidSource(leadSource)) return getSourceColor(leadSource!);
    if (isValidSource(deal.source)) return getSourceColor(deal.source);
    return '#D1D5DB';
  })();

  const sourceLetter = (() => {
    const src = leadSource || deal.source;
    if (src && src !== 'unspecified' && src.trim()) {
      return src.trim()[0].toUpperCase();
    }
    return '?';
  })();

  if (compact) {
    return (
      <div
        className={`dc-card dc-compact ${isDragging ? 'dc-dragging' : ''} ${isRotting ? 'dc-rotting' : ''}`}
        draggable
        onClick={onClick}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        style={{ '--source-color': sourceColor } as React.CSSProperties}
      >
        <div className="dc-compact-body">
          <div className="dc-compact-left">
            <span className="dc-compact-title">{displayName}</span>
            {isKit && <span className="dc-kit">Kit</span>}
          </div>
          <div className="dc-compact-right">
            <span className={`dc-days ${getDaysBadgeClass()}`}>{formatDays(daysInStage)}</span>
            <span className="dc-compact-value">{formatCurrency(displayValue ?? deal.value)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`dc-card ${isDragging ? 'dc-dragging' : ''} ${isRotting ? 'dc-rotting' : ''}`}
      draggable
      onClick={onClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{ '--source-color': sourceColor } as React.CSSProperties}
    >
      {/* Card body */}
      <div className="dc-body">
        {/* Title row */}
        <div className="dc-title-row">
          <span className="dc-title">{displayName}</span>
          {isKit && <span className="dc-kit">Kit</span>}
          {isRotting && (
            <span className="dc-warning" title="Deal needs attention">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" />
              </svg>
            </span>
          )}
        </div>

        {/* Contact / Organization */}
        {deal.contactName && (
          <div className="dc-contact">{deal.contactName}</div>
        )}

        {/* Footer: person icon + days badge + value */}
        <div className="dc-footer">
          <div className="dc-meta">
            <svg className="dc-person-icon" viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 1c-3.31 0-6 1.79-6 4v1h12v-1c0-2.21-2.69-4-6-4z" />
            </svg>
            {(() => {
              const designerName =
                deal.assignedDesignerName ||
                (deal.assignedArchitect && deal.assignedArchitectName ? deal.assignedArchitectName : '');
              if (!designerName) return null;
              return (
              <span className="dc-architect-badge" title={`Designer: ${designerName}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                  <path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" />
                </svg>
                {designerName.split(' ')[0]}
              </span>
              );
            })()}
            {deal.sniperZone && (
              <span className="dc-sniper-badge" title="In Sniper Zone">
                <Crosshair size={9} />
              </span>
            )}
            <span className="dc-source-badge" style={{ backgroundColor: sourceColor }}>{sourceLetter}</span>
            <span className={`dc-days ${getDaysBadgeClass()}`}>
              {formatDays(daysInStage)}
            </span>
            <span className="dc-value">{formatCurrency(displayValue ?? deal.value)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealCard;
